import { motion } from 'framer-motion'
import { useRouter } from 'next/router'
import React from 'react'

import ImageByWidthDevice from '@/components/ImageByWidthDevice'
import { introLeftVariants, introOpacityVariants, introRightVariants } from '@/config/const'
import { getWhySezInfrastructureConnectivity, getWhySezInfrastructureConnectivityList } from '@/services/why-sez'
import { useAppSelector } from '@/utils/hooks'

import Ornament from '~/assets/images/investment/why-invest/why-invest-ic-ornament.svg'

const InfrastructureConnectivity: React.FC = () => {
  const router = useRouter()
  const { data: infrastructureConnectivity } = useAppSelector(
    getWhySezInfrastructureConnectivity.select({
      headers: { 'Accept-Language': String(router.locale) },
    })
  )
  const { data: infrastructureConnectivityList } = useAppSelector(
    getWhySezInfrastructureConnectivityList.select({
      params: { skip: 0, limit: 1000 },
      headers: { 'Accept-Language': String(router.locale) },
    })
  )

  return (
    <div className='relative z-50 flex flex-col gap-8 bg-gradient-to-b from-white/0 from-0% via-white via-15% to-white pt-8 lg:gap-20 lg:pt-20'>
      {infrastructureConnectivity ? (
        <div className='container relative flex w-[1100px] flex-col items-center justify-center gap-6 lg:flex-row lg:gap-20'>
          <motion.div
            className='flex flex-col gap-4 lg:w-[497px] lg:gap-6'
            initial='hide'
            whileInView='show'
            exit='hide'
            variants={introLeftVariants}
            viewport={{ once: true }}
          >
            <h5 className='text-2xl	font-extrabold uppercase leading-[normal] text-gray-1 lg:text-[38px]'>
              {infrastructureConnectivity?.data.title1 ?? ''} &nbsp;
              <span className='text-red'> {infrastructureConnectivity?.data.title2 ?? ''}</span>
            </h5>

            <div
              className='leading-normal tracking-[-0.176px] text-gray-2'
              dangerouslySetInnerHTML={{ __html: infrastructureConnectivity?.data.description ?? '' }}
            />
          </motion.div>

          <motion.div
            className='relative h-auto w-full shrink-0 rounded-lg shadow-investment-flow lg:h-[300px] lg:w-[533px]'
            initial='hide'
            whileInView='show'
            exit='hide'
            variants={introRightVariants}
            viewport={{ once: true }}
          >
            {infrastructureConnectivity?.data.imageFiles && (
              <ImageByWidthDevice
                images={infrastructureConnectivity?.data.imageFiles}
                alt='tol image'
                className='h-full w-full rounded-lg object-cover object-center'
              />
            )}

            <div className='absolute -right-[119px] top-3 z-10 hidden lg:block'>
              <Ornament className='h-40 w-[70.018px]' />
            </div>
          </motion.div>
        </div>
      ) : null}
      {infrastructureConnectivityList && infrastructureConnectivityList?.data.metadata.count > 0 ? (
        <motion.div
          className='grid w-full grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
          initial='hide'
          whileInView='show'
          exit='hide'
          variants={introOpacityVariants}
          viewport={{ once: true }}
        >
          {infrastructureConnectivityList?.data.items.map((item) => (
            <div
              key={item.xid}
              className='group relative flex h-[220px] w-full flex-col items-center justify-center gap-[8.162px] transition-all duration-300'
            >
              <div className='absolute inset-0 transition-all duration-300 group-hover:bg-white group-hover:p-3'>
                <div className='absolute inset-0 bg-gradient-to-b from-black/0 to-black/80 transition-all duration-300 group-hover:m-3 group-hover:shadow-industry-zone' />
                <ImageByWidthDevice
                  images={item.imageFiles}
                  alt={item.imageFiles.desktop.fileName ?? ''}
                  className='h-full w-full object-cover object-center transition-all duration-300 group-hover:shadow-industry-zone'
                />
              </div>

              <p className='relative z-10 text-xl font-bold leading-normal text-orange lg:text-[17.595px]'>
                {item.title}
              </p>

              <div className='z-10 w-3 border-[1.257px] border-white transition-all duration-500 group-hover:w-30' />
            </div>
          ))}
        </motion.div>
      ) : null}
    </div>
  )
}

export default InfrastructureConnectivity
